import { Link } from 'react-router-dom';
import { LegalPageLayout } from '../../components/legal/LegalPageLayout';
import { LEGAL_CONTACT_EMAIL, LEGAL_OPERATOR_NAME } from '../../config/legal';

export function PrivacyPage() {
  const contactLink = <a href={`mailto:${LEGAL_CONTACT_EMAIL}`}>{LEGAL_CONTACT_EMAIL}</a>;

  return (
    <LegalPageLayout title="Privacy Policy" otherPage={{ to: '/terms', label: 'Terms of Service' }}>
      <section>
        <p>
          This policy explains what information VoteBeats collects, why, who it is shared with, and the choices you
          have. It covers the VoteBeats website and app, event pages that attendees open, and the VoteBeats Bridge
          desktop app.
        </p>
      </section>

      <section>
        <h2>1. Who we are</h2>
        <p>
          VoteBeats is operated by {LEGAL_OPERATOR_NAME} (&quot;VoteBeats&quot;, &quot;we&quot;, &quot;us&quot;). You
          can reach us about anything in this policy at {contactLink}.
        </p>
      </section>

      <section>
        <h2>2. Information DJs give us</h2>
        <p>If you use VoteBeats as a DJ, we collect:</p>
        <ul>
          <li>Account details: your name, email address and password. Passwords are held by our sign-in provider, Firebase Authentication; we never see them.</li>
          <li>Invite requests: the name, email address and note you send when you ask for an invite code.</li>
          <li>Profile: your display name, optional default contact name, phone and email, and an optional profile picture.</li>
          <li>Event details: event names, dates and times, location and address, description and contact information.</li>
          <li>Things you create for an event: messages to attendees, polls, prizes, games and uploaded audio files.</li>
          <li>Feature requests and roadmap votes.</li>
          <li>Information from VoteBeats Bridge, if you install it (see section 6).</li>
        </ul>
      </section>

      <section>
        <h2>3. Information attendees give us</h2>
        <p>
          Attendees don&apos;t create an account. When you open an event page, you are given a random anonymous ID
          through Firebase Anonymous Authentication so your requests and votes can be told apart from other
          people&apos;s. Everything else is optional and is only collected if you use the feature:
        </p>
        <ul>
          <li>a nickname</li>
          <li>song requests, with an optional dedication or note</li>
          <li>votes and rankings, song flags, and messages to the DJ</li>
          <li>event feedback (a rating and comments)</li>
          <li>answers in games such as trivia, predictions and dance-off votes, poll answers, and prize, lottery or roulette wins</li>
          <li>a cross-device code word, which we store only as a one-way hash</li>
          <li>a push-notification token, if you turn on notifications</li>
          <li>an on-site check-in, if the DJ uses it</li>
          <li>visit sessions, including your device type and your browser&apos;s user agent</li>
          <li>
            <strong>Next-event contact form (optional):</strong> if you ask about using VoteBeats at your own event, we
            collect the name, email address and/or phone number, preferred way to reach you, and answers you give, plus
            the event you asked from. This goes only to the VoteBeats team, not to the event&apos;s DJ, and is used only
            to follow up with you.
          </li>
        </ul>
        <p>
          The app does not record your IP address, although our hosting providers process it to deliver the site. We
          don&apos;t collect your location, and we don&apos;t use your camera or microphone.
        </p>
      </section>

      <section>
        <h2>4. What is public</h2>
        <p>
          Event pages are meant to be shared. Anyone with an event link can see the event&apos;s details, including the
          DJ&apos;s event contact information and location, and the request queue, including nicknames and
          dedications. Please don&apos;t put anything in a nickname, dedication or message that you wouldn&apos;t want
          others at the event to see.
        </p>
      </section>

      <section>
        <h2>5. Cookies and browser storage</h2>
        <p>
          VoteBeats does not set cookies. We use your browser&apos;s local storage and session storage to remember your
          anonymous ID, nickname and preferences, banners you have dismissed, vote cooldowns, and a draft of the
          create-event form. Firebase&apos;s sign-in and notification libraries keep their own data in your browser so
          that sign-in and notifications work.
        </p>
        <p>We don&apos;t use analytics or advertising trackers.</p>
      </section>

      <section>
        <h2>6. VoteBeats Bridge (desktop app)</h2>
        <p>Bridge is an optional app that DJs install to tell VoteBeats what is playing. When it runs, it:</p>
        <ul>
          <li>reads what your computer&apos;s media sessions report: the app, title, artist, album, position and play state, for every app Windows lists</li>
          <li>reads the window titles of music players</li>
          <li>on macOS, asks Spotify or Music for the current track</li>
          <li>sends the current track and playback state to your VoteBeats account</li>
          <li>stores your sign-in (encrypted by your operating system), your email address and your settings on your computer</li>
          <li>checks GitHub for updates</li>
        </ul>
      </section>

      <section>
        <h2>7. Who receives information</h2>
        <p>We use these service providers to run VoteBeats. Each receives only what it needs for its job:</p>
        <ul>
          <li><strong>Google Firebase and Google Cloud:</strong> hosting, database, sign-in, file storage, server functions, push notifications and sign-in emails.</li>
          <li><strong>Anthropic:</strong> song titles and artists, lyrics a DJ pastes, and trivia facts, for AI song analysis and trivia. We don&apos;t send attendee or DJ personal information.</li>
          <li><strong>Apple:</strong> the iTunes Search and Apple Music catalogs. Your search text is sent from your browser.</li>
          <li><strong>Odesli (song.link) and Spotify:</strong> song links only, to find the same song on other services.</li>
          <li><strong>MusicBrainz:</strong> song titles and artists.</li>
          <li><strong>Resend:</strong> delivers emails to DJs, to people who request an invite, and to the VoteBeats team when someone uses the contact form.</li>
          <li><strong>api.qrserver.com:</strong> generates QR codes and receives the link being encoded.</li>
          <li><strong>Google Fonts:</strong> serves the fonts the site uses.</li>
          <li><strong>Google Forms:</strong> receives a bug report if you choose to send one.</li>
          <li><strong>GitHub:</strong> hosts Bridge downloads and updates.</li>
        </ul>
        <p>We don&apos;t sell your information. We may disclose information if the law requires it.</p>
      </section>

      <section>
        <h2>8. How long we keep information</h2>
        <ul>
          <li>DJ accounts are kept until the DJ deletes them.</li>
          <li>
            <strong>
              Events a DJ deletes are removed from their account but kept by VoteBeats, with everything tied to them
              (requests, votes, messages, nicknames and feedback), for service history and analytics.
            </strong>{' '}
            This also applies to a DJ&apos;s events when they delete their account.
          </li>
          <li>Practice data (simulated requests used to try out the app) may be removed at any time.</li>
          <li>Contact form requests are kept for up to 12 months after we follow up, or until you ask us to delete them.</li>
          <li>Emails are kept by our email provider under its own retention rules.</li>
        </ul>
      </section>

      {/* Needs legal review before inviting DJs the owner doesn't know (COPPA) */}
      <section>
        <h2>9. Children and youth events</h2>
        <p>
          VoteBeats is used at youth events. Attendees don&apos;t create accounts and are only asked for an optional
          nickname. The optional contact form asks for contact details and is only for people 13 or older, or a parent
          or guardian filling it in for them. If you are under 13, please don&apos;t enter personal information such as your real name or contact
          details in a nickname, dedication or message. DJs and event hosts are responsible for running their events
          appropriately for their audience.
        </p>
        <p>
          Parents and guardians can contact us at {contactLink} to ask about or remove a child&apos;s information.
        </p>
      </section>

      <section>
        <h2>10. Your choices and requests</h2>
        <ul>
          <li>DJs can edit their profile and delete their account in Settings.</li>
          <li>Attendees can clear their code word and turn off notifications at any time.</li>
          <li>
            Anyone can email {contactLink} to ask for a copy of their information or for it to be deleted, where the
            law gives you that right. Where the law allows, we may keep deleted-event history as described in section
            8.
          </li>
        </ul>
      </section>

      <section>
        <h2>11. Security</h2>
        <p>
          We use established providers and encrypted connections to protect your information. No system is perfectly
          secure, so we can&apos;t guarantee that information will never be accessed without permission.
        </p>
      </section>

      <section>
        <h2>12. Changes to this policy</h2>
        <p>
          When we change this policy, we update the date at the top of this page. DJs are asked to accept the new
          version the next time they use VoteBeats. See also our <Link to="/terms">Terms of Service</Link>.
        </p>
      </section>
    </LegalPageLayout>
  );
}
